import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import WhatsAppButton from "../components/WhatsAppButton";
import { CONTACT } from "../data/contactInfo";
import { CHENNAI_AREA_PAGES, getAreaPath, getAreaSeo, getAreaSlugForName } from "../data/chennaiAreaPages";
import { CITY_SEO_PAGES, getCityPath, getCitySeo, getCitySlugForName } from "../data/citySeoPages";
import { getLocalityBySlug } from "../data/localitySeo";
import {
  getLocalityVenueNote,
  getLocalityBookingSteps,
  getLocalityWhyUs,
  getBreadcrumbSchema,
  getServiceSchema,
} from "../data/localityContent";
import { WEDDING_OCCASIONS, SITE_NAME, getLocalBusinessSchema, getFaqSchema } from "../data/seoData";
import { SEO_IMAGES } from "../data/seoImages";
import "../styles/SeoPages.css";

const LOCALITY_PATH_PREFIX = "chenda-melam-";

const ChendaMelamArea = () => {
  const { pageSlug } = useParams();
  const areaSlug = pageSlug?.startsWith(LOCALITY_PATH_PREFIX)
    ? pageSlug.slice(LOCALITY_PATH_PREFIX.length)
    : null;

  if (!areaSlug) {
    return <Navigate to="/" replace />;
  }

  const locality = getLocalityBySlug(areaSlug);

  if (!locality) {
    return <Navigate to="/chenda-melam-chennai" replace />;
  }

  const isArea = locality.kind === "area";
  const seo = isArea ? getAreaSeo(locality) : getCitySeo(locality);
  const displayName = locality.fullName
    ? `${locality.name} (${locality.fullName})`
    : locality.name;
  const locationLabel = isArea ? `${locality.name}, Chennai` : `${locality.name}, ${locality.state}`;
  const otherAreas = CHENNAI_AREA_PAGES.filter((a) => a.slug !== locality.slug);
  const sameStateCities = CITY_SEO_PAGES.filter(
    (c) => c.slug !== locality.slug && (!isArea ? c.state === locality.state : true)
  ).slice(0, 10);
  const venueNote = getLocalityVenueNote(locality);
  const bookingSteps = getLocalityBookingSteps(locality);
  const whyUs = getLocalityWhyUs(locality);

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        path={seo.path}
        ogImage={isArea ? SEO_IMAGES.area : SEO_IMAGES.city}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            getLocalBusinessSchema(),
            getFaqSchema(locality.faqs),
            getBreadcrumbSchema(locality, seo.path),
            getServiceSchema(locality, seo.path),
          ],
        }}
      />

      <main className="seo-page">
        <div className="seo-page-inner">
          <nav className="seo-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true"> / </span>
            <Link to="/chenda-melam-chennai">Wedding Melam</Link>
            <span aria-hidden="true"> / </span>
            <span>Chenda Melam {locality.name}</span>
          </nav>

          <header className="seo-hero">
            <span className="seo-eyebrow">
              {isArea ? `Kerala Chenda Melam for wedding · ${locality.name}` : `${locality.state} · ${locality.name}`}
            </span>
            <h1>Chenda Melam in {locality.name}</h1>
            <p className="seo-hero-lead">
              Book <strong>Chenda Melam in {locality.name}</strong> for your wedding — groom entry, bride entry,
              baraat, reception, and muhurtham. Based in {CONTACT.address.area}, Chennai, {SITE_NAME} provides a{" "}
              <strong>professional Kerala Chenda Melam team</strong> trusted across {displayName}
              {isArea ? " and greater Chennai" : ` and ${locality.state}`}.
            </p>
            <div className="seo-cta-row">
              <WhatsAppButton>WhatsApp Booking</WhatsAppButton>
              <a href={`tel:${CONTACT.phoneTel}`} className="seo-cta secondary">Call {CONTACT.phone}</a>
              <a href={`tel:${CONTACT.phone2Tel}`} className="seo-cta secondary">Call {CONTACT.phone2}</a>
              <Link to="/contact?type=wedding&form=open" className="seo-cta outline">Get Quote</Link>
            </div>
          </header>

          <section className="seo-section">
            <h2>Chenda Melam for Wedding in {locality.name}</h2>
            <p className="seo-text-block">{locality.highlight}</p>
            {locality.extra && <p className="seo-text-block">{locality.extra}</p>}
            <p className="seo-text-block">{venueNote}</p>
            <p className="seo-text-block">
              Looking for <strong>Chenda Melam near me in {locationLabel}</strong>? Choose a compact wedding-entry
              ensemble or a fuller <strong>Panchari Melam</strong> / <strong>Panchavadyam</strong>-inspired set for
              marriage functions across {locality.name}.
            </p>
          </section>

          <section className="seo-section">
            <h2>Why Families in {locality.name} Choose Us</h2>
            <div className="seo-grid-2">
              {whyUs.map((item) => (
                <article className="seo-card" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="seo-section">
            <h2>How to Book Chenda Melam in {locality.name}</h2>
            <ol className="seo-steps">
              {bookingSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>

          <section className="seo-section">
            <h2>Nearby Places We Also Serve</h2>
            <ul className="seo-tag-list">
              {locality.nearby.map((n) => {
                const nearbyAreaSlug = getAreaSlugForName(n);
                const nearbyCitySlug = getCitySlugForName(n);
                const href = nearbyAreaSlug
                  ? getAreaPath(nearbyAreaSlug)
                  : nearbyCitySlug
                    ? getCityPath(nearbyCitySlug)
                    : null;
                return (
                  <li key={n}>
                    {href ? (
                      <Link to={href}>Chenda Melam {n}</Link>
                    ) : (
                      <span>Chenda Melam near {n}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="seo-section">
            <h2>Wedding Melam Services in {locality.name}</h2>
            <div className="seo-grid-2">
              {WEDDING_OCCASIONS.slice(0, 6).map((o) => (
                <article className="seo-card" key={o.title}>
                  <h3>{o.title}</h3>
                  <p>{o.desc} Available for venues across {locality.name}.</p>
                </article>
              ))}
            </div>
          </section>

          <section className="seo-section">
            <h2>Chenda Melam Price in {locality.name}</h2>
            <p className="seo-text-block">
              Pricing for <strong>Chenda Melam in {locality.name}</strong> depends on team size, performance duration,
              and travel from our Chennai base. Share your venue and date for a clear quote — WhatsApp replies are usually fastest.
            </p>
          </section>

          <section className="seo-section seo-faq">
            <h2>FAQs — Chenda Melam {locality.name}</h2>
            {locality.faqs.map((item) => (
              <details className="seo-faq-item" key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </section>

          {isArea ? (
            <section className="seo-section">
              <h2>More Chennai Areas</h2>
              <nav className="seo-link-hub" aria-label="Chennai area pages">
                <Link to="/chenda-melam-chennai">All Chennai wedding Melam →</Link>
                {otherAreas.map((a) => (
                  <Link key={a.slug} to={getAreaPath(a.slug)}>Chenda Melam {a.name}</Link>
                ))}
              </nav>
            </section>
          ) : (
            <section className="seo-section">
              <h2>Other Cities We Cover</h2>
              <nav className="seo-link-hub" aria-label="City pages">
                <Link to="/chenda-melam-chennai">All South India wedding Melam →</Link>
                {sameStateCities.map((c) => (
                  <Link key={c.slug} to={getCityPath(c.slug)}>Chenda Melam {c.name}</Link>
                ))}
              </nav>
            </section>
          )}

          <section className="seo-section seo-internal-links">
            <h2>Related Pages</h2>
            <nav className="seo-link-hub">
              <Link to="/contact?type=wedding&form=open">Book Chenda Melam →</Link>
              <Link to="/fusions">Fusion Melam →</Link>
              <Link to="/chenda-classes-chennai">Chenda Melam Classes →</Link>
              <Link to="/melams">Melam Forms Guide →</Link>
              <Link to="/gallery">Gallery →</Link>
            </nav>
          </section>
        </div>
      </main>
    </>
  );
};

export default ChendaMelamArea;
